import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PlannerViewer } from "@/hooks/usePlannerPresence";

const MAX_SHOWN = 3;

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

interface PlannerPresenceAvatarsProps {
  self: PlannerViewer | null;
  viewers: PlannerViewer[];
}

function Avatar({
  viewer,
  label,
  showActivity = false,
}: {
  viewer: PlannerViewer;
  label: string;
  showActivity?: boolean;
}) {
  const tooltipText = showActivity && viewer.activity === 'editing'
    ? `${label} (editing)`
    : label;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative cursor-default select-none">
          <div className="w-7 h-7 rounded-full border-2 border-background overflow-hidden bg-muted flex items-center justify-center">
            {viewer.avatar_url ? (
              <img
                src={viewer.avatar_url}
                alt={label}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[10px] font-semibold text-muted-foreground leading-none">
                {getInitials(viewer.display_name)}
              </span>
            )}
          </div>
          {showActivity && (
            <span className="absolute -bottom-0.5 -right-0.5 text-[9px] leading-none">
              {viewer.activity === 'editing' ? '✏️' : '👁️'}
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

export function PlannerPresenceAvatars({ self, viewers }: PlannerPresenceAvatarsProps) {
  if (!self) return null;

  const shown = viewers.slice(0, MAX_SHOWN);
  const overflow = viewers.length - MAX_SHOWN;
  const overflowNames = viewers.slice(MAX_SHOWN).map((v) => v.display_name).join(", ");

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {/* Self — always first; no activity badge (you know what you're doing) */}
          <Avatar viewer={self} label="You" />

          {/* Other viewers — show activity emoji */}
          {shown.map((viewer) => (
            <Avatar key={viewer.user_id} viewer={viewer} label={viewer.display_name} showActivity />
          ))}

          {overflow > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-7 h-7 rounded-full border-2 border-background bg-muted flex items-center justify-center cursor-default select-none">
                  <span className="text-[11px] font-semibold text-muted-foreground">
                    +{overflow}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">{overflowNames}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
